import { db as defaultDb, type BookkeepingDB } from '@/db/db';
import { seedStarterAccounts } from '../accounts/service';
import { defaultCompanySettings } from '../company-settings/service';
import { registerDevice } from '../devices/service';
import { createFiscalYear } from '../fiscal-years/service';
import { seedDefaultPaymentSources } from '../payment-sources/service';
import { applySettingsPatch, getSettings } from '../settings/service';
import type { AccountingMethod, Company, CompanySettings, EntityType, FiscalYear, IsoDate, Uuid, VatPeriod } from '../types';
import { emptyDriveState } from './defaults';

export { emptyDriveState };

export interface CreateCompanyInput {
  name: string;
  orgNumber: string;
  entityType: EntityType;
  fiscalYearStart: IsoDate;
  fiscalYearEnd: IsoDate;
  /** Optional answers from /start/moms; entity defaults apply otherwise. */
  vatRegistered?: boolean;
  vatPeriod?: VatPeriod;
  accountingMethod?: AccountingMethod;
}

/**
 * Create a company with its first fiscal year, the starter chart of accounts,
 * default payment sources, company settings and an empty Drive state.
 */
export async function createCompany(
  input: CreateCompanyInput,
  db: BookkeepingDB = defaultDb,
): Promise<{ company: Company; fiscalYear: FiscalYear; settings: CompanySettings }> {
  return db.transaction(
    'rw',
    [db.companies, db.fiscalYears, db.accounts, db.accountEvents, db.settings, db.companySettings, db.paymentSources, db.driveState, db.devices],
    async () => {
      const now = new Date().toISOString();
      const company: Company = {
        id: crypto.randomUUID(),
        name: input.name.trim(),
        orgNumber: input.orgNumber.replace(/\D/g, ''),
        entityType: input.entityType,
        createdAt: now,
      };
      await db.companies.add(company);
      const fiscalYear = await createFiscalYear(
        { companyId: company.id, startDate: input.fiscalYearStart, endDate: input.fiscalYearEnd },
        db,
      );
      await seedStarterAccounts(company, fiscalYear.id, db);
      const sources = await seedDefaultPaymentSources(company, db);
      const bank = sources.find((s) => s.kind === 'bank') ?? null;
      const settings = defaultCompanySettings(company, now, {
        defaultPaymentSourceId: bank?.id ?? null,
        ...(input.vatRegistered !== undefined ? { vatRegistered: input.vatRegistered } : {}),
        ...(input.vatPeriod !== undefined ? { vatPeriod: input.vatPeriod } : {}),
        ...(input.accountingMethod !== undefined ? { accountingMethod: input.accountingMethod } : {}),
      });
      await db.companySettings.add(settings);
      await db.driveState.add(emptyDriveState(company.id));

      const appSettings = await getSettings(db);
      if (!appSettings.activeCompanyId) {
        await db.settings.put(applySettingsPatch(appSettings, { activeCompanyId: company.id }));
      }
      // The device that created the bookkeeping is the first one it knows.
      await registerDevice(company.id, db);
      return { company, fiscalYear, settings };
    },
  );
}

export async function setActiveCompany(companyId: Uuid | null, db: BookkeepingDB = defaultDb): Promise<void> {
  await db.transaction('rw', [db.settings, db.devices], async () => {
    await db.settings.put(applySettingsPatch(await getSettings(db), { activeCompanyId: companyId }));
    // Opening a restored bookkeeping on a new machine names it there too.
    if (companyId) await registerDevice(companyId, db);
  });
}

export async function listCompanies(db: BookkeepingDB = defaultDb): Promise<Company[]> {
  return db.companies.orderBy('name').toArray();
}

export const ENTITY_TYPE_LABELS: Record<EntityType, string> = {
  aktiebolag: 'Aktiebolag',
  enskild_firma: 'Enskild firma',
  ideell_forening: 'Ideell förening',
  bostadsrattsforening: 'Bostadsrättsförening',
};
